<template>
  <div
    ref="graphicEl"
    class="element slideshow"
    :class="[element.cssClasses]"
  >
    <template v-if="element.hasProgressIndicator">
      <progress-indicator
        v-if="entries && entries.length > 1"
        :index="timedIndex"
        :parent="element"
        :element="element.progressIndicator"
        :entries="entries"
      />
    </template>

    <graphic-element
      v-for="element in graphicElements"
      v-bind="$attrs"
      :key="`${element.id}:${element.slideIndex}`"
      :element="element"
      :graphic="graphic"
    />
  </div>
</template>

<script>
import { toRaw, defineAsyncComponent } from 'vue';

import GraphicsMixins from '../../../mixins/graphics-mixins';
import ProgressIndicator from './ProgressIndicator.vue';

export default {
  mixins: [GraphicsMixins],
  components: {
    // GraphicElement added in beforeCreate
    ProgressIndicator,
    GraphicElement: defineAsyncComponent(() => import('../../render-elements')), // eslint-disable-line
  },

  data() {
    return {
      slideIndex: 0,
      timedIndex: 0,
      current: null,
      timer: null,
      leave: 0,
      elements: [],
    };
  },

  computed: {
    entries() {
      return this.element.entries.filter((entry) => entry.meta.visible);
    },

    graphicElements() {
      return this.elements.map((el) => ({
        ...el,
        visible: el.slideIndex === this.slideIndex ? el.visible : false,
      }));
    },
  },

  watch: {
    entries: {
      deep: true,
      handler() {
        this.setFieldsKey();
        this.startTimer();
        this.initializeElements();

        // If number of entries decreased, make sure to reset slideIndex
        if (this.entries.length <= this.slideIndex) {
          this.slideIndex = 0;
        }
      },
    },
  },

  created() {
    this.setFieldsKey();
    this.startTimer();
    this.initializeElements();
  },

  beforeUnmount() {
    clearTimeout(this.timer);
  },

  methods: {
    /**
     * Initialize the elements for the slideshow.
     * This will create a copy of the element's elements (lol) for each entry,
     * and set the slideIndex on each element.
     * This slideIndex is used to determine which elements are visible.
     */
    initializeElements() {
      const elements = [];

      for (let i = 0; i < this.entries.length; i += 1) {
        const copy = structuredClone(toRaw(this.element.elements));

        copy.forEach((el) => {
          el.slideIndex = i; // eslint-disable-line no-param-reassign
        });

        elements.push(...copy);
      }

      const setOverrides = (els) => {
        els.forEach((el) => {
          if (this.fields[el.slideIndex] && this.fields[el.slideIndex][el.id]) {
            Object.assign(el, this.fields[el.slideIndex][el.id]);
          }

          if (el.elements) {
            setOverrides(el.elements);
          }
        });
      };

      // Recursively set overrides for all elements
      setOverrides(elements);
      this.elements = elements.reverse();
    },

    beforeEnter() {
      // We need to wait to change the progress bar til we're done loading the next slides
      this.timedIndex = this.slideIndex;
      this.$emit('before-enter');
    },

    calcLeaveDuration() {
      if (!this.current || !this.current.length) {
        return;
      }

      this.leave = this.current
        .concat(this.current.reduce((arr, elem) => arr.concat(elem.elements || []), []))
        .flatMap((elem) => (elem.animation ? Number(elem.animationOutDuration) + Number(elem.animationOutDelay) : 0))
        .reduce((a, b) => ((b >= a) ? b : a));

      this.afterLeave();
    },

    /**
     * Set root overrides on elements.
     */
    setFieldsKey() {
      this.fields = [];

      const setFields = (elements, index) => {
        elements.forEach((element) => {
          this.fields[index] = this.fields[index] || {};
          this.fields[index][element.template_id] = element;

          if (element.entries) {
            setFields(element.entries, index);
          }
        });
      };

      this.entries.forEach((entry, index) => {
        setFields(entry.fields, index);
      });
    },

    startTimer() {
      this.timer = setTimeout(() => {
        clearTimeout(this.timer);
        this.advance();
      }, this.element.cycleTime);
    },

    advance() {
      if (this.entries.length) {
        if (this.slideIndex >= this.entries.length - 1) {
          this.slideIndex = 0;
        } else {
          this.slideIndex += 1;
        }
      }

      this.startTimer();
    },
  },
};
</script>
